import { ProvidersApi, PROVIDER_SELECT_FIELDS } from '../api/providersApi';
import { NewProviderPayload, Provider, TripQuota, ProviderHistory, TripLimitStatus, TripLimitUpdatePayload } from '../types/providers.types';
import { logger } from '@/shared/lib/logger';
import {
    getServerCalendarDate,
    getServerWeekEnd,
    formatServerStartOfDay,
    formatServerEndOfDay,
} from '@/shared/utils/serverTimeContext';

/**
 * Calculates trip limit status based on provider's trip limits and actual trip counts
 */
const calculateTripLimitStatus = (provider: any, tripQuotas?: TripQuota[]): TripLimitStatus => {
    if (!tripQuotas || tripQuotas.length === 0) {
        return { isOverLimit: false };
    }

    // Map span types to provider limit fields
    const spanTypeMapping: Record<string, { field: string; displayName: string }> = {
        'DAY': { field: 'daily_trip_limit_max', displayName: 'daily' },
        'WEEK': { field: 'weekly_trip_limit_max', displayName: 'weekly' },
        'MONTH': { field: 'monthly_trip_limit_max', displayName: 'monthly' },
        'YEAR': { field: 'yearly_trip_limit_max', displayName: 'yearly' },
    };

    // Check each trip quota against its corresponding limit
    for (const quota of tripQuotas) {
        const mapping = spanTypeMapping[quota.span_type];
        if (!mapping) continue;

        const limitValue = provider[mapping.field];
        
        // If a limit is defined and the total trip count exceeds it
        if (limitValue !== null && limitValue !== undefined && limitValue > 0) {
            const currentCount = quota.total_trip_count || 0;
            
            if (currentCount >= limitValue) {
                return {
                    isOverLimit: true,
                    limitType: mapping.displayName,
                    currentCount,
                    limitValue,
                    warningMessage: `Over ${mapping.displayName} limit: ${currentCount}/${limitValue} trips`,
                };
            }
        }
    }

    return { isOverLimit: false };
};

const formatProviderContactAddress = (provider: Record<string, unknown>): string => {
    const streetAddress = typeof provider.street_address === 'string' ? provider.street_address.trim() : '';
    const city = typeof provider.city === 'string' ? provider.city.trim() : '';
    const postcode = typeof provider.postcode === 'string' ? provider.postcode.trim() : '';

    const locality = [city, postcode].filter(Boolean).join(' ');
    return [streetAddress, locality].filter(Boolean).join(', ');
};

const transformProviderData = (
    providerValues: any[],
    preferencesMap: Map<number, string>,
    tripQuotasMap?: Map<number, TripQuota[]>
): Provider => {
    const providerObject: { [key: string]: any } = {};
    PROVIDER_SELECT_FIELDS.forEach((field, index) => {
        providerObject[field] = providerValues[index];
    });

    const preferenceId = providerObject['preference.id'];
    const priority = preferenceId ? preferencesMap.get(preferenceId) : undefined;
    
    // Get trip quotas for this provider
    const providerId = providerObject.id;
    const tripQuotas = tripQuotasMap?.get(providerId) || [];
    const tripLimitStatus = calculateTripLimitStatus(providerObject, tripQuotas);

    return {
        ...providerObject,
        id: String(providerObject.id),
        priority,
        contact_address: formatProviderContactAddress(providerObject),
        tripLimitStatus,
    } as Provider;
};

export const createProvidersService = (providersApi: ProvidersApi) => {
    const createProvider = async (providerData: NewProviderPayload) => {
        try {
            const response = await providersApi.createProvider(providerData);
            return response;
        } catch (error) {
            throw new Error('Failed to create provider.');
        }
    };

    const getProviders = async (params: { page: number, rowsPerPage: number, sortConfig: any, query?: unknown[] }): Promise<{ results: Provider[], total: number }> => {
        try {
            const response = await providersApi.getProviders(params);
            const results = response.data;

            if (!Array.isArray(results) || results.length === 0) {
                return { results: [], total: 0 };
            }

            // Get preference IDs
            const preferenceIds = results.map(provider => provider[PROVIDER_SELECT_FIELDS.indexOf('preference.id')])
                                          .filter(id => id !== null && id !== undefined) as number[];

            let preferencesMap = new Map<number, string>();
            if (preferenceIds.length > 0) {
                const uniquePreferenceIds = [...new Set(preferenceIds)] as number[];
                const preferencesResponse = await providersApi.getCompanyPreferences(uniquePreferenceIds);
                if (preferencesResponse.data) {
                    preferencesMap = new Map(preferencesResponse.data.map(pref => [pref[0] as number, pref[1] as string]));
                }
            }

            // Get provider IDs
            const providerIds = results.map(provider => provider[PROVIDER_SELECT_FIELDS.indexOf('id')]) as number[];

            // Fetch trip quotas for ALL providers in a single batched API call
            // (previously made one call per provider — N+1 problem)
            let tripQuotasMap = new Map<number, TripQuota[]>();
            if (providerIds.length > 0) {
                try {
                    const batchResponse = await providersApi.getCompanyTripCountsBatch(providerIds);
                    for (const tripCount of (batchResponse.data || [])) {
                        const companyId = tripCount.company?.id;
                        if (companyId !== undefined && companyId !== null) {
                            const existing = tripQuotasMap.get(companyId) ?? [];
                            existing.push(tripCount);
                            tripQuotasMap.set(companyId, existing);
                        }
                    }
                } catch {
                    // Non-fatal: trip limit warnings will be suppressed if this fails
                }
            }

            const transformedResults = results.map(providerValues =>
                transformProviderData(providerValues, preferencesMap, tripQuotasMap)
            );

            return { results: transformedResults, total: response.info?.total_results || 0 };
        } catch (error) {
            throw new Error('Failed to fetch providers.');
        }
    };

    const getProviderDetailsById = async (id: string): Promise<Provider | null> => {
        try {
            const response = await providersApi.getProviderDetailsById(id);
            if (!response.data || response.data.length === 0) {
                return null;
            }

            const providerData = response.data[0];
            const preferenceId = providerData[PROVIDER_SELECT_FIELDS.indexOf('preference.id')];

            let preferencesMap = new Map<number, string>();
            if (preferenceId !== null && preferenceId !== undefined) {
                const preferencesResponse = await providersApi.getCompanyPreferences([preferenceId as number]);
                if (preferencesResponse.data) {
                    preferencesMap = new Map(preferencesResponse.data.map(pref => [pref[0] as number, pref[1] as string]));
                }
            }

            // Fetch trip quotas for this provider
            let tripQuotasMap = new Map<number, TripQuota[]>();
            try {
                const quotasResponse = await providersApi.getCompanyTripCounts(id);
                const providerId = providerData[PROVIDER_SELECT_FIELDS.indexOf('id')] as number;
                tripQuotasMap.set(providerId, quotasResponse.data || []);
            } catch (error) {
            }

            return transformProviderData(providerData, preferencesMap, tripQuotasMap);
        } catch (error) {
            throw new Error(`Failed to fetch provider details for id ${id}`);
        }
    };

    const getTripQuotas = async (
        companyId: string,
        tripLimitStartDate?: string | null,
        tripLimitEndDate?: string | null,
    ): Promise<TripQuota[]> => {
        try {
            const response = await providersApi.getCompanyTripCounts(companyId);
            const storedQuotas: TripQuota[] = response.data || [];
            logger.debug('[TripQuota] stored quotas:', storedQuotas.length, 'tripLimitStart:', tripLimitStartDate, 'tripLimitEnd:', tripLimitEndDate);

            // Gate 3: mirror RoR's `add_future_trips_count(rs, ...) if rs.entries.any?`
            // (app/support/company_single_trip_count_support.rb line 14). If the
            // provider has no persisted k3_company_trip_count rows (i.e. no
            // weekly/monthly/yearly limit has ever been saved via the threshold
            // form) then RoR renders the section as empty and the view-side
            // `result_set.present?` check hides it entirely. Return [] here so
            // the caller can skip rendering the Trip quota table.
            if (storedQuotas.length === 0) {
                logger.debug('[TripQuota] no persisted company_trip_count rows — skipping live counts (matches RoR Gate 3)');
                return [];
            }

            const serverToday = getServerCalendarDate();
            const { year, month, day } = serverToday;
            const toIso = (y: number, m: number, d: number, h: number, min: number, s: number) => {
                const pad = (n: number) => String(n).padStart(2, '0');
                return `${y}-${pad(m)}-${pad(d)}T${pad(h)}:${pad(min)}:${pad(s)}`;
            };
            const todayStartIso = formatServerStartOfDay(serverToday);
            const todayEndIso = formatServerEndOfDay(serverToday);

            const endOfWeek = getServerWeekEnd(serverToday);
            const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();

            const clampStart = (base: string): string => {
                if (tripLimitStartDate && tripLimitStartDate > base.slice(0, 10)) {
                    return `${tripLimitStartDate}T00:00:00`;
                }
                return base;
            };
            const clampEnd = (base: string): string => {
                if (tripLimitEndDate && tripLimitEndDate < base.slice(0, 10)) {
                    return `${tripLimitEndDate}T23:59:59`;
                }
                return base;
            };

            const spanRanges: { spanType: string; start: string; end: string }[] = [
                { spanType: 'DAY',   start: clampStart(todayStartIso), end: clampEnd(todayEndIso) },
                { spanType: 'WEEK',  start: clampStart(todayStartIso), end: clampEnd(toIso(endOfWeek.year, endOfWeek.month, endOfWeek.day, 23, 59, 59)) },
                { spanType: 'MONTH', start: clampStart(todayStartIso), end: clampEnd(toIso(year, month, daysInMonth, 23, 59, 59)) },
                { spanType: 'YEAR',  start: clampStart(todayStartIso), end: clampEnd(toIso(year, 12, 31, 23, 59, 59)) },
            ];

            const numericId = parseInt(companyId, 10);
            logger.debug('[TripQuota] fetching live counts for spans:', spanRanges.map(s => `${s.spanType}: ${s.start} → ${s.end}`));
            const liveCounts = await Promise.all(
                spanRanges.map(({ spanType, start, end }) =>
                    providersApi.getLiveTripCounts(numericId, start, end, spanType)
                        .then(counts => { logger.debug(`[TripQuota] live ${spanType}:`, counts); return { spanType, ...counts }; })
                        .catch((err) => { logger.error(`[TripQuota] live ${spanType} FAILED:`, err); return { spanType, assigned: 0, completed: 0, total: 0 }; })
                )
            );

            // Merge: for each span type, find stored quota and add live counts
            const SPAN_ORDER = ['DAY', 'WEEK', 'MONTH', 'YEAR'];
            const mergedQuotas: TripQuota[] = SPAN_ORDER.map(spanType => {
                const stored = storedQuotas.find(q => q.span_type === spanType);
                const live = liveCounts.find(l => l.spanType === spanType);

                if (spanType === 'DAY') {
                    // DAY: entirely from live counts (no stored daily record)
                    return {
                        id: stored?.id ?? 0,
                        span_type: spanType as TripQuota['span_type'],
                        assigned_trip_count: live?.assigned ?? 0,
                        completed_trip_count: live?.completed ?? 0,
                        total_trip_count: live?.total ?? 0,
                        company: stored?.company ?? { id: numericId },
                    };
                }

                // WEEK/MONTH/YEAR: stored + live
                return {
                    id: stored?.id ?? 0,
                    span_type: spanType as TripQuota['span_type'],
                    assigned_trip_count: (stored?.assigned_trip_count ?? 0) + (live?.assigned ?? 0),
                    completed_trip_count: (stored?.completed_trip_count ?? 0) + (live?.completed ?? 0),
                    total_trip_count: (stored?.total_trip_count ?? 0) + (live?.total ?? 0),
                    company: stored?.company ?? { id: numericId },
                };
            });

            logger.debug('[TripQuota] MERGED result:', mergedQuotas.map(q =>
                `${q.span_type}: total=${q.total_trip_count} assigned=${q.assigned_trip_count} completed=${q.completed_trip_count}`
            ));
            return mergedQuotas;
        } catch (error) {
            logger.error('[TripQuota] getTripQuotas FAILED:', error);
            throw new Error(`Failed to fetch trip quotas for company ${companyId}`);
        }
    };

    const getProviderHistory = async (providerId: string, timePeriod?: string, startDate?: string, endDate?: string): Promise<ProviderHistory[]> => {
        try {
            const { data } = await providersApi.getProviderHistory(providerId, timePeriod, startDate, endDate);
            return data || [];
        } catch (error) {
            throw new Error(`Failed to fetch history for provider ${providerId}`);
        }
    };

    const updateProvider = async (providerId: string, providerData: Partial<NewProviderPayload>) => {
        try {
            const response = await providersApi.updateProvider(providerId, providerData);
            return response;
        } catch (error) {
            throw new Error('Failed to update provider.');
        }
    };

    const updateTripLimit = async (companyId: number, tripLimitData: TripLimitUpdatePayload) => {
        try {
            const response = await providersApi.updateTripLimit(companyId, tripLimitData);
            return response;
        } catch (error) {
            throw new Error('Failed to update trip limit.');
        }
    };

        const checkProviderExists = async (name: string): Promise<boolean> => {
            try {
                const response = await providersApi.checkProviderExists(name);
                return response.data && response.data.length > 0;
            } catch (error) {
                logger.error('Provider existence check failed:', error);
                throw new Error('Failed to verify provider name existence.');
            }
        };

        /**
         * Check whether an external_provider_type is already taken by another provider (ECOPD-14326).
         * Returns the name of the conflicting provider, or null if the type is available.
         * @param externalProviderType - The external provider type value to check
         * @param excludeProviderId - Optional provider ID to exclude from the check (for updates)
         */
        const checkExternalProviderTypeUnique = async (
            externalProviderType: string,
            excludeProviderId?: number,
        ): Promise<string | null> => {
            try {
                const response = await providersApi.checkExternalProviderTypeUnique(
                    externalProviderType,
                    excludeProviderId,
                );
                if (response.data && response.data.length > 0) {
                    return (response.data[0][1] as string) || 'Another provider';
                }
                return null;
            } catch (error) {
                logger.error('External provider type uniqueness check failed:', error);
                // If check fails, let backend validate — return null (no conflict detected)
                return null;
            }
        };
    
        return {
            createProvider,
            getProviders,
            getProviderDetailsById,
            getTripQuotas,
            getProviderHistory,
            updateProvider,
            updateTripLimit,
            checkProviderExists,
            checkExternalProviderTypeUnique,
        };
    };
